import { z } from 'zod';

// ══════════════════════════════════════════════════════════════
// SETTINGS SCHEMA — Single source of truth for all configurable values
// ══════════════════════════════════════════════════════════════

export const settingsSchema = z.object({
    // ── General ──
    timezone: z.string().default('America/Mexico_City'),

    // ── Operaciones (Flujo Quirúrgico) ──
    patientInitialLifetimeHours: z.number().min(1).max(72).default(3),
    patientRenewalHours: z.number().min(1).max(24).default(12),
    patientPostExitHours: z.number().min(1).max(12).default(5),
    requireDoctorName: z.boolean().default(false),
    requireProcedureName: z.boolean().default(false),
    enableSubStatuses: z.boolean().default(true),

    // ── Experiencia Familiar ──
    showPatientName: z.boolean().default(true),
    showProcedureName: z.boolean().default(true),
    allowFamilyNotifications: z.boolean().default(true),
    customWelcomeMessage: z.string().default(''),
    showProgressBar: z.boolean().default(true),
    publicVisibility: z.enum(['public', 'pin', 'private']).default('pin'),
    receptionPin: z.string().default(''),
    googleReviewUrl: z.string().default(''),

    // ── Pantallas y Publicidad ──
    adsEnabled: z.boolean().default(false),
    showAdsOnTV: z.boolean().default(true),
    showAdsOnMobile: z.boolean().default(true),
    hideAdsDuringSurgery: z.boolean().default(true),
    fullscreenWhenEmpty: z.boolean().default(true),
    fullscreenIntervalSec: z.number().min(3).max(120).default(10),
    tickerEnabled: z.boolean().default(true),
    tickerSpeed: z.enum(['slow', 'normal', 'fast']).default('normal'),

    // ── Seguridad ──
    maxLoginAttempts: z.number().min(1).max(20).default(5),
    lockoutDurationMin: z.number().min(1).max(1440).default(30),
    sessionTimeoutMin: z.number().min(0).max(1440).default(0), // 0 = disabled
});

/** Full settings object with all keys populated */
export type Settings = z.infer<typeof settingsSchema>;

/** Partial settings — what gets stored per-tier (only overrides) */
export type SettingsOverrides = Partial<Settings>;

// ── Tier visibility: what each admin level can do with a setting ──
export type TierAccess = 'edit' | 'readonly' | 'hidden';

// ── Metadata for each setting (UI labels, sections, descriptions, permissions) ──
export interface SettingMeta {
    key: keyof Settings;
    label: string;
    description: string;
    section: 'general' | 'operations' | 'family' | 'screens' | 'security';
    type: 'boolean' | 'number' | 'string' | 'enum';
    enumOptions?: { value: string; label: string }[];
    min?: number;
    max?: number;
    unit?: string;
    placeholder?: string;
    /** Presets for number fields — renders as pill buttons instead of free input */
    presets?: number[];
    /** Tier-level access control */
    tiers: { superadmin: TierAccess; group: TierAccess; hospital: TierAccess };
}

// ── Timezone options for Mexico/LATAM ──
export const TIMEZONE_OPTIONS = [
    { value: 'America/Mexico_City', label: 'Centro (CDMX, MTY, GDL)' },
    { value: 'America/Monterrey', label: 'Monterrey' },
    { value: 'America/Cancun', label: 'Sureste (Cancún, Mérida)' },
    { value: 'America/Tijuana', label: 'Noroeste (Tijuana, Mexicali)' },
    { value: 'America/Hermosillo', label: 'Pacífico (Hermosillo, Sonora)' },
    { value: 'America/Mazatlan', label: 'Pacífico (Mazatlán, Sinaloa)' },
    { value: 'America/Chihuahua', label: 'Sierra (Chihuahua)' },
    { value: 'America/Bogota', label: 'Colombia (Bogotá)' },
    { value: 'America/Lima', label: 'Perú (Lima)' },
    { value: 'America/Santiago', label: 'Chile (Santiago)' },
];

export const SETTINGS_CATALOG: SettingMeta[] = [
    // ── General ──
    { key: 'timezone', label: 'Zona Horaria', description: 'Zona horaria para reportes y cron jobs.', section: 'general', type: 'enum',
      enumOptions: TIMEZONE_OPTIONS,
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'readonly' } },

    // ── Operations ──
    { key: 'patientInitialLifetimeHours', label: 'Vida Inicial del Paciente', description: 'Horas antes de que un paciente inactivo sea auto-archivado.', section: 'operations', type: 'number', min: 1, max: 72, unit: 'horas',
      presets: [3, 6, 12, 24],
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },
    { key: 'patientRenewalHours', label: 'Renovación por Actividad', description: 'Horas que se extienden al actualizar el estado de un paciente.', section: 'operations', type: 'number', min: 1, max: 24, unit: 'horas',
      presets: [6, 12, 24],
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },
    { key: 'patientPostExitHours', label: 'Visibilidad Post-Alta', description: 'Horas que el paciente permanece visible después de EXIT.', section: 'operations', type: 'number', min: 1, max: 12, unit: 'horas',
      presets: [1, 3, 5, 8],
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },
    { key: 'requireDoctorName', label: 'Requerir Doctor', description: 'Exigir nombre del doctor al registrar paciente.', section: 'operations', type: 'boolean',
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },
    { key: 'requireProcedureName', label: 'Requerir Procedimiento', description: 'Exigir nombre del procedimiento al registrar paciente.', section: 'operations', type: 'boolean',
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },
    { key: 'enableSubStatuses', label: 'Sub-estados en Cirugía', description: 'Permitir detalle de Incisión, Cierre, etc. durante cirugía.', section: 'operations', type: 'boolean',
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },

    // ── Family Experience ──
    { key: 'showPatientName', label: 'Mostrar Nombre del Paciente', description: 'Mostrar el nombre del paciente en la recepción digital y vista familiar.', section: 'family', type: 'boolean',
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },
    { key: 'showProcedureName', label: 'Mostrar Procedimiento', description: 'Mostrar el nombre del procedimiento a las familias.', section: 'family', type: 'boolean',
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },
    { key: 'allowFamilyNotifications', label: 'Notificaciones Push', description: 'Permitir que las familias reciban notificaciones cuando cambia el estado.', section: 'family', type: 'boolean',
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },
    { key: 'customWelcomeMessage', label: 'Mensaje de Bienvenida', description: 'Texto personalizado que aparece en la vista familiar.', section: 'family', type: 'string',
      placeholder: 'Ej: Bienvenido a Christus Muguerza',
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },
    { key: 'showProgressBar', label: 'Barra de Progreso', description: 'Mostrar barra de progreso visual del procedimiento a las familias.', section: 'family', type: 'boolean',
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },
    { key: 'publicVisibility', label: 'Acceso a Recepción', description: 'Quién puede ver la recepción digital del hospital.', section: 'family', type: 'enum',
      enumOptions: [{ value: 'public', label: 'Público (cualquiera)' }, { value: 'pin', label: 'Con PIN de visitante' }, { value: 'private', label: 'Privado (solo staff)' }],
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },
    { key: 'receptionPin', label: 'PIN de Recepción', description: 'PIN de acceso para visitantes en la sala de espera (4-6 dígitos).', section: 'family', type: 'string',
      placeholder: 'Ej: 1234',
      tiers: { superadmin: 'hidden', group: 'hidden', hospital: 'edit' } },
    { key: 'googleReviewUrl', label: 'Google Reviews URL', description: 'Link de Google Maps para recolectar reseñas de 5 estrellas post-cirugía.', section: 'family', type: 'string',
      placeholder: 'https://g.page/r/tu-hospital/review',
      tiers: { superadmin: 'hidden', group: 'hidden', hospital: 'edit' } },

    // ── Screens & Ads ──
    { key: 'adsEnabled', label: 'Publicidad Activa', description: 'Activar el sistema de publicidad en esta sucursal.', section: 'screens', type: 'boolean',
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'hidden' } },
    { key: 'showAdsOnTV', label: 'Ads en TVs', description: 'Mostrar publicidad en las pantallas de TV del hospital.', section: 'screens', type: 'boolean',
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },
    { key: 'showAdsOnMobile', label: 'Ads en Celulares', description: 'Mostrar publicidad en la vista familiar (celular).', section: 'screens', type: 'boolean',
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },
    { key: 'hideAdsDuringSurgery', label: 'Ocultar Ads en Cirugía', description: 'No mostrar publicidad cuando hay una cirugía activa.', section: 'screens', type: 'boolean',
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },
    { key: 'fullscreenWhenEmpty', label: 'Slideshow sin Pacientes', description: 'Cuando no hay pacientes activos, la TV muestra un slideshow de contenido.', section: 'screens', type: 'boolean',
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },
    { key: 'fullscreenIntervalSec', label: 'Intervalo de Slides', description: 'Tiempo entre slides del modo slideshow.', section: 'screens', type: 'number', min: 3, max: 120, unit: 'seg',
      presets: [5, 10, 15, 30, 60],
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },
    { key: 'tickerEnabled', label: 'Ticker Activo', description: 'Mostrar la barra de texto deslizante en la parte inferior de las TVs.', section: 'screens', type: 'boolean',
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },
    { key: 'tickerSpeed', label: 'Velocidad del Ticker', description: 'Velocidad de desplazamiento de la barra de texto.', section: 'screens', type: 'enum',
      enumOptions: [{ value: 'slow', label: 'Lento' }, { value: 'normal', label: 'Normal' }, { value: 'fast', label: 'Rápido' }],
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'edit' } },

    // ── Security ──
    { key: 'maxLoginAttempts', label: 'Intentos de Login', description: 'Máximo de intentos fallidos antes de bloqueo temporal.', section: 'security', type: 'number', min: 1, max: 20,
      tiers: { superadmin: 'edit', group: 'readonly', hospital: 'hidden' } },
    { key: 'lockoutDurationMin', label: 'Duración del Bloqueo', description: 'Minutos de espera tras agotar los intentos de login.', section: 'security', type: 'number', min: 1, max: 1440, unit: 'min',
      tiers: { superadmin: 'edit', group: 'readonly', hospital: 'hidden' } },
    { key: 'sessionTimeoutMin', label: 'Timeout de Sesión', description: 'Cerrar sesión automáticamente tras inactividad. 0 = desactivado.', section: 'security', type: 'number', min: 0, max: 1440, unit: 'min',
      tiers: { superadmin: 'edit', group: 'edit', hospital: 'readonly' } },
];

export const SECTION_LABELS: Record<string, { label: string; icon: string }> = {
    general: { label: 'General', icon: '🌐' },
    operations: { label: 'Operaciones', icon: '🩺' },
    family: { label: 'Experiencia Familiar', icon: '👨‍👩‍👧' },
    screens: { label: 'Pantallas y Publicidad', icon: '📺' },
    security: { label: 'Seguridad', icon: '🔐' },
};

// ══════════════════════════════════════════════════════════════
// GLOBAL DEFAULTS — Computed from Zod schema defaults
// ══════════════════════════════════════════════════════════════

/** Returns a full Settings object with all defaults populated */
export function getGlobalDefaults(): Settings {
    return settingsSchema.parse({});
}

// ══════════════════════════════════════════════════════════════
// RESOLVE SETTINGS — 3-tier merge (global → group → hospital)
// ══════════════════════════════════════════════════════════════

export interface ResolvedSettings {
    /** The final merged settings object */
    effective: Settings;
    /** For each key, where did the value come from? */
    origins: Record<keyof Settings, 'default' | 'global' | 'group' | 'hospital'>;
}

/**
 * Merges settings from 3 tiers with cascading priority.
 * Each tier only stores its OVERRIDES (partial). 
 * The merge is: Zod Defaults ← Global ← Group ← Hospital
 */
export function resolveSettings(
    globalOverrides: SettingsOverrides,
    groupOverrides: SettingsOverrides | null,
    hospitalOverrides: SettingsOverrides | null
): ResolvedSettings {
    const defaults = getGlobalDefaults();
    const origins: Record<string, 'default' | 'global' | 'group' | 'hospital'> = {};

    // Start with all defaults
    const effective: any = { ...defaults };
    for (const key of Object.keys(defaults) as (keyof Settings)[]) {
        origins[key] = 'default';
    }

    // Layer 1: Global overrides
    if (globalOverrides) {
        for (const [key, value] of Object.entries(globalOverrides)) {
            if (value !== undefined && value !== null) {
                effective[key] = value;
                origins[key] = 'global';
            }
        }
    }

    // Layer 2: Group overrides
    if (groupOverrides) {
        for (const [key, value] of Object.entries(groupOverrides)) {
            if (value !== undefined && value !== null) {
                effective[key] = value;
                origins[key] = 'group';
            }
        }
    }

    // Layer 3: Hospital overrides (highest priority)
    if (hospitalOverrides) {
        for (const [key, value] of Object.entries(hospitalOverrides)) {
            if (value !== undefined && value !== null) {
                effective[key] = value;
                origins[key] = 'hospital';
            }
        }
    }

    return {
        effective: settingsSchema.parse(effective),
        origins: origins as ResolvedSettings['origins'],
    };
}

// ══════════════════════════════════════════════════════════════
// HELPER — Load & resolve from D1 in one call
// ══════════════════════════════════════════════════════════════

/**
 * Loads all three tiers from D1 and returns resolved settings.
 * This is the function you call from any endpoint that needs config.
 */
export async function resolveSettingsForHospital(
    db: any,
    hospitalId: string
): Promise<ResolvedSettings> {
    // 1. Get hospital row (need groupId + local overrides)
    const hospital: any = await db.prepare(
        'SELECT groupId, settings FROM hospitals WHERE id = ?'
    ).bind(hospitalId).first();

    if (!hospital) {
        // Hospital not found — return pure defaults
        return resolveSettings({}, null, null);
    }

    // 2. Get global overrides
    const globalRow: any = await db.prepare(
        "SELECT settings FROM platform_settings WHERE id = 'global'"
    ).first();

    // 3. Get group overrides (if hospital belongs to a group)
    let groupOverrides: SettingsOverrides | null = null;
    if (hospital.groupId) {
        const groupRow: any = await db.prepare(
            'SELECT settings FROM hospital_groups WHERE id = ?'
        ).bind(hospital.groupId).first();
        if (groupRow?.settings) {
            try { groupOverrides = JSON.parse(groupRow.settings); } catch { groupOverrides = null; }
        }
    }

    // Parse JSON safely
    let globalParsed: SettingsOverrides = {};
    let hospitalParsed: SettingsOverrides = {};

    try { globalParsed = globalRow?.settings ? JSON.parse(globalRow.settings) : {}; } catch {}
    try { hospitalParsed = hospital.settings ? JSON.parse(hospital.settings) : {}; } catch {}

    return resolveSettings(globalParsed, groupOverrides, hospitalParsed);
}
